import {
  brandName,
  type Brand,
  type Folder,
  type Mailbox,
  type Message,
  type OutboxEntry,
  type RecipientWarning,
  type Thread,
  type ThreadDetail,
  type ThreadMutation,
  type ThreadQuery,
} from '@gandhi/mail-protocol';
import { ApiError, type MailApiClient } from '../api/client.js';
import {
  draftRecipients,
  forwardDraft,
  isBrandChange,
  newDraft,
  rebrandDraft,
  replyDraft,
  toOutgoing,
  type ComposeDraft,
} from './compose.js';
import { createStore, type Store } from './store.js';

export type ThemeMode = 'light' | 'dark';

export interface PendingBrandChange {
  readonly fromBrand: Brand;
  readonly toBrand: Brand;
  readonly message: string;
  /** Set when the change was triggered by switching mailbox rather than by sending. */
  readonly targetMailboxId?: string;
}

export interface MailState {
  readonly loading: boolean;
  readonly error: string | null;
  readonly mailboxes: readonly Mailbox[];
  readonly activeMailboxId: string | null;
  readonly folders: readonly Folder[];
  readonly activeFolderId: string | null;
  readonly threads: readonly Thread[];
  readonly threadTotal: number;
  readonly activeThread: ThreadDetail | null;
  /** Set the moment a row is clicked, so selection does not wait for the body. */
  readonly activeThreadId: string | null;
  readonly search: string;
  readonly unreadOnly: boolean;
  readonly flaggedOnly: boolean;
  readonly draft: ComposeDraft | null;
  readonly draftSaved: boolean;
  /** A refused send, shown verbatim inside compose. The server's wording names both brands. */
  readonly sendError: string | null;
  readonly selectedThreadIds: readonly string[];
  readonly warnings: readonly RecipientWarning[];
  readonly brandChange: PendingBrandChange | null;
  readonly outbox: readonly OutboxEntry[];
  readonly sending: boolean;
  readonly themeMode: ThemeMode;
}

/** One screenful. Paging is real: `threadTotal` is the server's count, not this. */
export const THREAD_PAGE_SIZE = 25;

export const initialState: MailState = {
  loading: false,
  error: null,
  mailboxes: [],
  activeMailboxId: null,
  folders: [],
  activeFolderId: null,
  threads: [],
  threadTotal: 0,
  activeThread: null,
  activeThreadId: null,
  search: '',
  unreadOnly: false,
  flaggedOnly: false,
  draft: null,
  draftSaved: false,
  sendError: null,
  selectedThreadIds: [],
  warnings: [],
  brandChange: null,
  outbox: [],
  sending: false,
  themeMode: 'light',
};

export function activeMailbox(state: MailState): Mailbox | null {
  return state.mailboxes.find((m) => m.id === state.activeMailboxId) ?? null;
}

export interface BrandGroup {
  readonly brandId: Brand;
  readonly mailboxes: readonly Mailbox[];
}

// Cached per state object: React compares snapshots by identity, so a selector that
// built a fresh array every call would re-render forever.
const brandGroupCache = new WeakMap<MailState, BrandGroup[]>();

export function mailboxesByBrand(state: MailState): BrandGroup[] {
  const cached = brandGroupCache.get(state);
  if (cached !== undefined) return cached;
  const groups = new Map<Brand, Mailbox[]>();
  for (const mailbox of state.mailboxes) {
    groups.set(mailbox.brandId, [...(groups.get(mailbox.brandId) ?? []), mailbox]);
  }
  const result = [...groups.entries()].map(([brandId, mailboxes]) => ({ brandId, mailboxes }));
  brandGroupCache.set(state, result);
  return result;
}

export function pendingOutbox(state: MailState): OutboxEntry[] {
  return state.outbox.filter((e) => e.status === 'queued');
}

export interface MailControllerOptions {
  readonly api: MailApiClient;
  readonly initial?: Partial<MailState>;
  readonly pageSize?: number;
}

/**
 * All of the client's behaviour, with the DOM left out so it can be tested directly.
 *
 * The brand rules live here rather than in components: the active mailbox is the only
 * thing that decides the sending identity, and moving a draft between mailboxes has to
 * pass through `confirmBrandChange`.
 */
export class MailController {
  readonly store: Store<MailState>;
  readonly #api: MailApiClient;
  readonly #pageSize: number;
  /** Conversations already fetched. A message body does not change once it has arrived. */
  readonly #bodies = new Map<string, ThreadDetail>();
  #outboxTimer: ReturnType<typeof setInterval> | undefined;

  constructor(options: MailControllerOptions) {
    this.#api = options.api;
    this.#pageSize = options.pageSize ?? THREAD_PAGE_SIZE;
    this.store = createStore<MailState>({ ...initialState, ...options.initial });
  }

  get state(): MailState {
    return this.store.getState();
  }

  #set(patch: Partial<MailState>): void {
    this.store.setState(patch);
  }

  async #guard(work: () => Promise<void>): Promise<void> {
    this.#set({ loading: true, error: null });
    try {
      await work();
    } catch (error) {
      this.#set({ error: error instanceof Error ? error.message : String(error) });
    } finally {
      this.#set({ loading: false });
    }
  }

  async init(): Promise<void> {
    await this.#guard(async () => {
      const { mailboxes } = await this.#api.mailboxes();
      this.#set({ mailboxes });
      const first = mailboxes[0];
      if (first !== undefined) await this.#loadMailbox(first.id);
      await this.refreshOutbox();
    });
  }

  async #loadMailbox(mailboxId: string): Promise<void> {
    const { folders } = await this.#api.folders(mailboxId);
    const inbox = folders.find((f) => f.role === 'inbox') ?? folders[0];
    this.#bodies.clear();
    this.#set({
      activeMailboxId: mailboxId,
      folders,
      activeFolderId: inbox?.id ?? null,
      activeThread: null,
      activeThreadId: null,
      selectedThreadIds: [],
      threads: [],
      search: '',
    });
    await this.refreshThreads();
  }

  /**
   * Switching mailbox switches brand, and with it the identity anything composed next
   * leaves as. With a draft open that is a brand change, so it stops for confirmation
   * rather than letting a half-written message quietly follow the mailbox.
   */
  async selectMailbox(mailboxId: string): Promise<void> {
    const { draft, mailboxes } = this.state;
    const target = mailboxes.find((m) => m.id === mailboxId);
    if (target === undefined) return;
    if (draft !== null && draft.brandId !== target.brandId) {
      const fromBrand = draft.threadBrandId ?? draft.brandId;
      this.#set({
        brandChange: {
          fromBrand,
          toBrand: target.brandId,
          message:
            draft.threadBrandId === undefined
              ? draftChangeMessage(fromBrand, target.brandId)
              : threadChangeMessage(fromBrand, target.brandId),
          targetMailboxId: mailboxId,
        },
      });
      return;
    }
    await this.#guard(() => this.#loadMailbox(mailboxId));
  }

  async selectFolder(folderId: string): Promise<void> {
    this.#set({
      activeFolderId: folderId,
      activeThread: null,
      activeThreadId: null,
      selectedThreadIds: [],
      threads: [],
    });
    await this.refreshThreads();
  }

  async setSearch(search: string): Promise<void> {
    this.#set({ search, selectedThreadIds: [], threads: [] });
    await this.refreshThreads();
  }

  async setFilters(filters: { unreadOnly?: boolean; flaggedOnly?: boolean }): Promise<void> {
    this.#set({
      unreadOnly: filters.unreadOnly ?? this.state.unreadOnly,
      flaggedOnly: filters.flaggedOnly ?? this.state.flaggedOnly,
      selectedThreadIds: [],
      threads: [],
    });
    await this.refreshThreads();
  }

  #threadQuery(): ThreadQuery {
    const { activeFolderId, search, unreadOnly, flaggedOnly } = this.state;
    return {
      ...(activeFolderId === null || search !== '' ? {} : { folderId: activeFolderId }),
      ...(search === '' ? {} : { search }),
      ...(unreadOnly ? { unreadOnly } : {}),
      ...(flaggedOnly ? { flaggedOnly } : {}),
    };
  }

  /** Reloads every page already on screen, so a mutation does not collapse the list. */
  async refreshThreads(): Promise<void> {
    const { activeMailboxId, threads } = this.state;
    if (activeMailboxId === null) return;
    await this.#guard(async () => {
      const page = await this.#api.threads(activeMailboxId, {
        ...this.#threadQuery(),
        limit: Math.max(this.#pageSize, threads.length),
        offset: 0,
      });
      this.#set({ threads: page.threads, threadTotal: page.total });
    });
  }

  async loadMoreThreads(): Promise<void> {
    const { activeMailboxId, threads, threadTotal } = this.state;
    if (activeMailboxId === null || threads.length >= threadTotal) return;
    await this.#guard(async () => {
      const page = await this.#api.threads(activeMailboxId, {
        ...this.#threadQuery(),
        limit: this.#pageSize,
        offset: threads.length,
      });
      this.#set({
        threads: [...this.state.threads, ...page.threads],
        threadTotal: page.total,
      });
    });
  }

  toggleThreadSelection(threadId: string): void {
    const { selectedThreadIds } = this.state;
    this.#set({
      selectedThreadIds: selectedThreadIds.includes(threadId)
        ? selectedThreadIds.filter((id) => id !== threadId)
        : [...selectedThreadIds, threadId],
    });
  }

  selectAllThreads(selected: boolean): void {
    this.#set({ selectedThreadIds: selected ? this.state.threads.map((t) => t.id) : [] });
  }

  /** Applies one mutation to every selected conversation, then clears the selection. */
  async applyToSelection(mutation: ThreadMutation): Promise<void> {
    const { activeMailboxId, selectedThreadIds } = this.state;
    if (activeMailboxId === null || selectedThreadIds.length === 0) return;
    await this.#guard(async () => {
      for (const threadId of selectedThreadIds) {
        await this.#api.mutateThread(activeMailboxId, threadId, mutation);
      }
      this.#set({ selectedThreadIds: [] });
      await this.refreshThreads();
      await this.refreshFolders();
    });
  }

  async refreshFolders(): Promise<void> {
    const { activeMailboxId } = this.state;
    if (activeMailboxId === null) return;
    const { folders } = await this.#api.folders(activeMailboxId);
    this.#set({ folders });
  }

  /**
   * Opening is local work. Selection is applied before anything is asked of the server,
   * a body already fetched is shown from the cache, and marking the conversation read
   * happens here and is sent afterwards — so the click costs at most one round trip,
   * and none at all for a conversation opened before.
   */
  async openThread(threadId: string): Promise<void> {
    const { activeMailboxId } = this.state;
    if (activeMailboxId === null) return;
    const cached = this.#bodies.get(threadId);
    this.#set({
      activeThreadId: threadId,
      ...(cached === undefined ? {} : { activeThread: cached }),
    });
    this.#markRead(activeMailboxId, threadId);
    if (cached !== undefined) return;
    try {
      const thread = await this.#api.thread(activeMailboxId, threadId);
      const read = this.state.threads.find((t) => t.id === threadId)?.unread === false;
      this.#remember(read ? asRead(thread) : thread);
      if (this.state.activeThreadId === threadId) {
        this.#set({ activeThread: this.#bodies.get(threadId) ?? thread });
      }
    } catch (error) {
      this.#set({ error: error instanceof Error ? error.message : String(error) });
    }
  }

  #remember(thread: ThreadDetail): void {
    this.#bodies.set(thread.id, thread);
  }

  /** Read state is the reader's, not the server's: it is applied now and sent after. */
  #markRead(mailboxId: string, threadId: string): void {
    if (this.state.threads.find((t) => t.id === threadId)?.unread !== true) return;
    this.#applyLocally(threadId, { unread: false });
    void this.#api
      .mutateThread(mailboxId, threadId, { unread: false })
      .then(() => this.refreshFolders())
      .catch(() => this.refreshThreads());
  }

  #applyLocally(threadId: string, mutation: { unread?: boolean; flagged?: boolean }): void {
    const { threads, activeThread } = this.state;
    const apply = <T extends { id: string; unread: boolean; flagged: boolean }>(thread: T): T => ({
      ...thread,
      unread: mutation.unread ?? thread.unread,
      flagged: mutation.flagged ?? thread.flagged,
    });
    const cached = this.#bodies.get(threadId);
    if (cached !== undefined) this.#remember(apply(cached));
    this.#set({
      threads: threads.map((t) => (t.id === threadId ? apply(t) : t)),
      ...(activeThread?.id === threadId ? { activeThread: apply(activeThread) } : {}),
    });
  }

  closeThread(): void {
    this.#set({ activeThread: null, activeThreadId: null });
  }

  /**
   * Read and flag are shown at once and sent afterwards. A move is not: it takes the
   * conversation out of the folder being looked at, so the list has to come back.
   */
  async setThreadFlags(
    threadId: string,
    mutation: { unread?: boolean; flagged?: boolean; folderId?: string },
  ): Promise<void> {
    const { activeMailboxId, activeThread } = this.state;
    if (activeMailboxId === null) return;
    if (mutation.folderId === undefined) {
      this.#applyLocally(threadId, mutation);
      try {
        await this.#api.mutateThread(activeMailboxId, threadId, mutation);
        await this.refreshFolders();
      } catch {
        await this.refreshThreads();
      }
      return;
    }
    await this.#guard(async () => {
      await this.#api.mutateThread(activeMailboxId, threadId, mutation);
      this.#bodies.delete(threadId);
      if (activeThread?.id === threadId) this.#set({ activeThread: null, activeThreadId: null });
      await this.refreshThreads();
      await this.refreshFolders();
    });
  }

  /**
   * `prefill` names a recipient and subject only. There is no way to reach the mailbox or
   * the brand through it, so a `mailto:` in a message cannot pick who the reply comes from.
   */
  composeNew(prefill: { to?: string; subject?: string } = {}): void {
    const mailbox = activeMailbox(this.state);
    if (mailbox === null) return;
    this.#set({
      draft: { ...newDraft(mailbox), ...prefill },
      warnings: [],
      draftSaved: false,
      sendError: null,
    });
  }

  reply(message: Message, all = false): void {
    const mailbox = activeMailbox(this.state);
    const thread = this.state.activeThread;
    if (mailbox === null || thread === null) return;
    this.#set({
      draft: replyDraft({ mailbox, thread, message, all }),
      warnings: [],
      draftSaved: false,
      sendError: null,
    });
  }

  forward(message: Message): void {
    const mailbox = activeMailbox(this.state);
    const thread = this.state.activeThread;
    if (mailbox === null || thread === null) return;
    this.#set({
      draft: forwardDraft({ mailbox, thread, message }),
      warnings: [],
      draftSaved: false,
      sendError: null,
    });
  }

  /** Replies to the newest message in the open conversation. What the keyboard uses. */
  replyToOpenThread(all = false): void {
    const thread = this.state.activeThread;
    const latest = thread?.messages[thread.messages.length - 1];
    if (latest !== undefined) this.reply(latest, all);
  }

  forwardOpenThread(): void {
    const thread = this.state.activeThread;
    const latest = thread?.messages[thread.messages.length - 1];
    if (latest !== undefined) this.forward(latest);
  }

  updateDraft(patch: Partial<ComposeDraft>): void {
    const { draft } = this.state;
    if (draft === null) return;
    this.#set({ draft: { ...draft, ...patch }, draftSaved: false });
  }

  /**
   * Autosave. A draft the server will not take is not worth interrupting anyone over,
   * so a failure leaves the indicator unsaved and the message unaffected.
   */
  async saveDraft(): Promise<void> {
    const { draft } = this.state;
    if (draft === null || isDraftEmpty(draft)) return;
    try {
      const message = {
        ...toOutgoing(draft),
        ...(draft.attachments.length === 0 ? { attachments: [] } : {}),
      };
      const { draft: saved } =
        draft.draftId === undefined
          ? await this.#api.createDraft(draft.mailboxId, message)
          : await this.#api.updateDraft(draft.mailboxId, draft.draftId, message);
      if (this.state.draft !== draft) return;
      this.#set({ draft: { ...draft, draftId: saved.id }, draftSaved: true });
    } catch {
      this.#set({ draftSaved: false });
    }
  }

  closeCompose(): void {
    const { draft } = this.state;
    if (draft?.draftId !== undefined) {
      void this.#api.deleteDraft(draft.mailboxId, draft.draftId).catch(() => undefined);
    }
    this.#set({ draft: null, warnings: [], brandChange: null, draftSaved: false, sendError: null });
  }

  /** Contacts do not exist yet; the BFF answers from Sent mail. */
  async refreshWarnings(): Promise<void> {
    const { draft } = this.state;
    if (draft === null) return;
    const recipients = draftRecipients(draft);
    if (recipients.length === 0) {
      this.#set({ warnings: [] });
      return;
    }
    try {
      const { warnings } = await this.#api.recipientWarnings(draft.mailboxId, recipients);
      this.#set({ warnings });
    } catch {
      this.#set({ warnings: [] });
    }
  }

  async confirmBrandChange(): Promise<void> {
    const { brandChange, draft, mailboxes } = this.state;
    if (brandChange === null || draft === null) return;
    this.#set({ brandChange: null });

    if (brandChange.targetMailboxId !== undefined) {
      const target = mailboxes.find((m) => m.id === brandChange.targetMailboxId);
      if (target === undefined) return;
      if (draft.draftId !== undefined) {
        void this.#api.deleteDraft(draft.mailboxId, draft.draftId).catch(() => undefined);
      }
      this.#set({ draft: rebrandDraft(draft, target), draftSaved: false });
      await this.#guard(() => this.#loadMailbox(target.id));
      await this.refreshWarnings();
      return;
    }

    await this.sendDraft({ fromBrand: brandChange.fromBrand, toBrand: brandChange.toBrand });
  }

  cancelBrandChange(): void {
    this.#set({ brandChange: null });
  }

  /**
   * Sends as the draft's mailbox and nothing else. A rejected brand lock comes back
   * from the server with both brand names, which is what the dialog then shows — the
   * client never invents that text for a change the server has not seen.
   */
  async sendDraft(acknowledgement?: { fromBrand: Brand; toBrand: Brand }): Promise<boolean> {
    const { draft } = this.state;
    if (draft === null) return false;

    if (acknowledgement === undefined && isBrandChange(draft)) {
      const fromBrand = draft.threadBrandId as Brand;
      this.#set({
        brandChange: {
          fromBrand,
          toBrand: draft.brandId,
          message: threadChangeMessage(fromBrand, draft.brandId),
        },
      });
      return false;
    }

    this.#set({ sending: true, error: null, sendError: null });
    try {
      const { entry } = await this.#api.send(draft.mailboxId, {
        ...toOutgoing(draft, acknowledgement),
        ...(draft.draftId === undefined ? {} : { draftId: draft.draftId }),
      });
      this.#set({
        draft: null,
        warnings: [],
        draftSaved: false,
        outbox: [...this.state.outbox, entry],
      });
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.code === 'BRAND_LOCK_VIOLATION') {
        const fromBrand = error.details['fromBrand'] as Brand;
        const toBrand = error.details['toBrand'] as Brand;
        this.#set({ brandChange: { fromBrand, toBrand, message: error.message } });
        return false;
      }
      this.#set({ sendError: error instanceof Error ? error.message : String(error) });
      return false;
    } finally {
      this.#set({ sending: false });
    }
  }

  async undoSend(entryId: string): Promise<void> {
    await this.#guard(async () => {
      await this.#api.cancelSend(entryId);
      await this.refreshOutbox();
    });
  }

  async refreshOutbox(): Promise<void> {
    const { entries } = await this.#api.outbox();
    this.#set({ outbox: entries });
  }

  setThemeMode(themeMode: ThemeMode): void {
    this.#set({ themeMode });
  }

  startOutboxPolling(intervalMs = 1000): void {
    if (this.#outboxTimer !== undefined) return;
    this.#outboxTimer = setInterval(() => {
      void this.refreshOutbox().catch(() => undefined);
    }, intervalMs);
  }

  stopOutboxPolling(): void {
    if (this.#outboxTimer === undefined) return;
    clearInterval(this.#outboxTimer);
    this.#outboxTimer = undefined;
  }
}

function asRead(thread: ThreadDetail): ThreadDetail {
  return {
    ...thread,
    unread: false,
    messages: thread.messages.map((message) => ({ ...message, unread: false })),
  };
}

function isDraftEmpty(draft: ComposeDraft): boolean {
  return (
    draft.to === '' &&
    draft.cc === '' &&
    draft.bcc === '' &&
    draft.subject === '' &&
    draft.bodyHtml === '' &&
    draft.attachments.length === 0
  );
}

function threadChangeMessage(fromBrand: Brand, toBrand: Brand): string {
  return `This conversation arrived as ${brandName(fromBrand)}. Continuing it here sends as ${brandName(toBrand)}, and the recipient sees ${brandName(toBrand)}.`;
}

function draftChangeMessage(fromBrand: Brand, toBrand: Brand): string {
  return `You started this message as ${brandName(fromBrand)}. Moving it here sends it as ${brandName(toBrand)}, from your ${brandName(toBrand)} address.`;
}
